import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarCheck, CircleDollarSign, Download, HandCoins, Hourglass, Inbox, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, fmtUSD } from "@/lib/api";

function ExportButton({ months }: { months: string[] }) {
  const [month, setMonth] = useState(months[months.length - 1]);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `legendary-${month}.xlsx` });
      a.click(); URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("No se pudo generar el Excel"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2" data-testid="export-controls">
      <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="export-month-select" className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700">
        {[...months].reverse().map((m) => <option key={m} value={m}>{label(m)}</option>)}
      </select>
      <button onClick={download} disabled={busy} data-testid="export-download-btn" className="inline-flex items-center gap-2 rounded-full bg-[#0B192C] px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Exportar Excel
      </button>
    </div>
  );
}

interface MonthRow { month: string; bookings: number; requests: number; gross: number; collected: number; refunded: number; receivable: number }
interface Stats { months: MonthRow[]; totals: { bookings: number; requests: number; gross: number; collected: number; refunded: number; receivable: number; upcoming: number; pending_balances: number }; top_yachts: { yacht: string; bookings: number; gross: number }[] }

const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const label = (k: string) => `${MONTHS_ES[Number(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}`;

function Kpi({ icon: Icon, title, value, sub, testId, accent }: { icon: React.ElementType; title: string; value: string; sub?: string; testId: string; accent?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between"><p className="text-[11px] uppercase tracking-wider text-slate-500">{title}</p><Icon className={`h-4 w-4 ${accent || "text-[#00A8A0]"}`} /></div>
      <p className="mt-3 font-display text-3xl text-[#0B192C]">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { data, isLoading } = useQuery<Stats>({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats", { params: { months: 12 } })).data });
  if (isLoading || !data) return <Loader2 className="h-6 w-6 animate-spin text-[#00A8A0]" />;
  const t = data.totals;
  const chart = data.months.map((m) => ({ ...m, name: label(m.month) }));
  const cur = new Date().toISOString().slice(0, 7);
  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#0B192C]">Ingresos</h1>
          <p className="mt-2 text-sm text-slate-500">Por mes del paseo. "Cobrado" = anticipos + pagos de saldo en línea (menos reembolsos); "Por cobrar" = saldos pendientes de reservas pagadas y reservas confirmadas manualmente.</p>
        </div>
        <ExportButton months={data.months.map((m) => m.month)} />
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Kpi icon={CircleDollarSign} title="Valor reservado" value={fmtUSD(t.gross)} sub={`${t.bookings} reservas pagadas/confirmadas`} testId="kpi-gross" />
        <Kpi icon={HandCoins} title="Cobrado en línea" value={fmtUSD(t.collected)} sub={t.refunded > 0 ? `Reembolsado ${fmtUSD(t.refunded)}` : "Anticipos + saldos"} testId="kpi-collected" />
        <Kpi icon={Hourglass} title="Por cobrar" value={fmtUSD(t.receivable)} sub={`${t.pending_balances} saldos pendientes`} testId="kpi-receivable" accent="text-[#D4AF37]" />
        <Kpi icon={CalendarCheck} title="Próximos paseos" value={String(t.upcoming)} sub="Reservas con fecha futura" testId="kpi-upcoming" />
        <Kpi icon={Inbox} title="Solicitudes abiertas" value={String(t.requests)} sub="Nuevas o contactadas" testId="kpi-requests" accent="text-sky-600" />
      </div>
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="revenue-chart">
        <p className="text-sm font-semibold text-[#0B192C]">Cobrado vs. por cobrar · últimos 12 meses</p>
        <div className="mt-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ left: 8, right: 8 }}>
              <CartesianGrid vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} width={44} />
              <Tooltip formatter={(v: number) => fmtUSD(v)} contentStyle={{ borderRadius: 8, borderColor: "#E2E8F0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="collected" name="Cobrado" stackId="a" fill="#0B192C" radius={[0, 0, 0, 0]} />
              <Bar dataKey="receivable" name="Por cobrar" stackId="a" fill="#D4AF37" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="revenue-table">
          <table className="w-full min-w-[560px] text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/70">
              <th className="px-4 py-3">Mes</th><th className="px-4 py-3 text-right">Reservas</th><th className="px-4 py-3 text-right">Valor</th><th className="px-4 py-3 text-right">Cobrado</th><th className="px-4 py-3 text-right">Por cobrar</th><th className="px-4 py-3 text-right">Solicitudes</th>
            </tr></thead>
            <tbody>
              {[...data.months].reverse().map((m) => (
                <tr key={m.month} data-testid={`revenue-row-${m.month}`} className={`border-t border-slate-100 ${m.month === cur ? "bg-[#F3E5AB]/20" : ""}`}>
                  <td className="px-4 py-2.5 font-medium text-[#0B192C]">{label(m.month)}</td>
                  <td className="px-4 py-2.5 text-right">{m.bookings}</td>
                  <td className="px-4 py-2.5 text-right">{fmtUSD(m.gross)}</td>
                  <td className="px-4 py-2.5 text-right text-[#008C86] font-semibold">{fmtUSD(m.collected)}</td>
                  <td className="px-4 py-2.5 text-right text-[#B8962E] font-semibold">{fmtUSD(m.receivable)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{m.requests}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="top-yachts">
          <p className="text-sm font-semibold text-[#0B192C]">Yates con más ingresos</p>
          {data.top_yachts.length === 0 ? <p className="mt-3 text-sm text-slate-500">Aún no hay reservas pagadas.</p> : (
            <ul className="mt-4 space-y-3">
              {data.top_yachts.map((y, i) => (
                <li key={y.yacht} className="flex items-center gap-3 text-sm" data-testid={`top-yacht-${i}`}>
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-[#0B192C] text-xs font-semibold text-[#D4AF37]">{i + 1}</span>
                  <span className="flex-1 truncate text-[#0B192C]">{y.yacht}</span>
                  <span className="text-xs text-slate-500">{y.bookings}×</span>
                  <span className="font-semibold text-[#0B192C]">{fmtUSD(y.gross)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
